import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Briefcase, TrendingUp, Award, Clock, GraduationCap, Mail, ArrowRight } from 'lucide-react';

const Careers = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: 'Growing National Business',
      description: 'Join a rapidly expanding brokerage with offices in Darwin, Melbourne, Brisbane and Sydney. Be part of a business that is shaping the future of lending in Australia.',
    },
    {
      icon: Award,
      title: 'Award-Winning Culture',
      description: 'Work alongside recognised industry leaders in a team that celebrates excellence and supports each other to achieve outstanding results.',
    },
    {
      icon: Clock,
      title: 'Flexible Working',
      description: 'We understand that work-life balance matters. Enjoy flexible arrangements that allow you to deliver your best work, your way.',
    },
    {
      icon: GraduationCap,
      title: 'Professional Development',
      description: 'Continuous learning is at our core. Access training, mentorship, and development opportunities to grow your career in finance and lending.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Careers"
        description="Join the Manage Your Loans team. We're hiring mortgage brokers and support professionals across Darwin, Melbourne, Brisbane and Sydney."
        path="/careers"
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <Briefcase className="w-8 h-8 text-[#99DF63]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Careers at Manage Your Loans</h1>
            <p className="text-lg text-gray-200">
              Build your career with an award-winning team making a difference for Australians
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <p className="text-lg text-gray-600 leading-relaxed">
              We're always looking for passionate professionals to join our growing team across Darwin, Melbourne, Brisbane and Sydney. At Manage Your Loans, you'll work in a supportive, innovative environment where your contributions genuinely help Australians achieve their financial goals.
            </p>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">Why Work With Us</h2>
              <p className="text-gray-600 text-lg">What makes Manage Your Loans a great place to build your career</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="w-12 h-12 bg-[#99DF63]/20 rounded-lg flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-[#00594C]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#00594C] mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Current Openings */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">Current Opportunities</h2>
            </div>
            <div className="bg-gray-50 rounded-xl p-8 border border-gray-100 text-center">
              <p className="text-gray-600 leading-relaxed text-lg">
                We're always interested in hearing from talented mortgage brokers and support professionals. If you're passionate about helping Australians achieve their financial goals, we'd love to hear from you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#00594C] mb-4">Interested in Joining Us?</h2>
            <p className="text-gray-600 text-lg mb-8">
              Send your resume and a brief cover letter to our team. We'd love to learn more about you.
            </p>
            <a
              href="mailto:admin@manageyourloans.com.au"
              className="inline-flex items-center px-8 py-4 bg-[#00594C] text-white font-semibold rounded-full hover:bg-[#003d35] transition-colors duration-300"
            >
              <Mail className="w-5 h-5 mr-2" />
              admin@manageyourloans.com.au
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;
